import { useCallback } from "react";
import { executeQuery } from "../../api/QueryExecution";
import { getEquipment } from "../../api/EquipmentMasters";
import {
  StandardRequestGroup,
  StandardRequestRow,
  groupStandardRequests,
  rankStandardRequests,
} from "./standardRequestGrouping";

/**
 * US 820773 — Create Work Order from Standard Request (Mobile).
 *
 * Retrieves Standard Requests via the system-defined query "SYS Standard
 * Request" and groups them client-side exactly like the Web app. The pure
 * ranking/grouping logic lives in ./standardRequestGrouping (import-free, unit
 * tested); this hook is the I/O wrapper around it.
 */

// Re-export the pure grouping API so existing consumers can keep importing from
// this hook module.
export {
  SR_RANK_LABEL_KEYS,
  rankStandardRequests,
  groupStandardRequests,
} from "./standardRequestGrouping";
export type {
  StandardRequestRank,
  StandardRequestRow,
  StandardRequestGroup,
} from "./standardRequestGrouping";

export interface StandardRequestsResult {
  success: boolean;
  groups: StandardRequestGroup[];
  /** Raw QwColumn metadata from the query, for dynamic column rendering if needed. */
  columns: any[];
  /** Flat ranked+sorted rows (ungrouped), useful for search/quick-filter. */
  rows: StandardRequestRow[];
  messages: any[];
}

const SR_QUERY_NAME = "SYS Standard Request";
const SR_MODULE_NAME = "Work Order";

export default function useStandardRequests() {
  /**
   * Runs the SYS Standard Request query and returns grouped/sorted results.
   * @param equipment Optional equipment to group against. When empty, every SR
   *                  falls into the "Other" group (matches Web).
   */
  const getStandardRequests = useCallback(
    async (equipment?: string): Promise<StandardRequestsResult> => {
      const eq = (equipment ?? "").trim();

      // Resolve the selected equipment's class for rank-2 grouping. This is a
      // best-effort lookup; if it fails, SRs simply won't group by class.
      let equipmentClass = "";
      if (eq) {
        try {
          const eqResp = await getEquipment({ equipment: eq });
          if (eqResp?.status === 200 && !eqResp.isError) {
            equipmentClass = eqResp.data?.Class ?? "";
          }
        } catch {
          equipmentClass = "";
        }
      }

      const response = await executeQuery({
        QueryName: SR_QUERY_NAME,
        ModuleName: SR_MODULE_NAME,
        EnableMultiPlantExecution: false,
        IsPublicQuery: true,
      });

      if (
        response.status !== 200 ||
        response.isError ||
        !response.data?.Success
      ) {
        return {
          success: false,
          groups: [],
          columns: [],
          rows: [],
          messages: response.data?.Messages ?? [],
        };
      }

      const rawRows: StandardRequestRow[] = response.data.QueryResult ?? [];
      const columns: any[] = response.data.QueryData?.QwColumn ?? [];
      const rows = rankStandardRequests(rawRows, eq, equipmentClass);
      const groups = groupStandardRequests(rawRows, eq, equipmentClass);

      return {
        success: true,
        groups,
        columns,
        rows,
        messages: response.data.Messages ?? [],
      };
    },
    []
  );

  return { getStandardRequests };
}
